<script setup>
  import { ref, computed, watchEffect, onMounted } from 'vue';
  import { useProForecastStore } from '@/stores/ProForecastStore';
  import { isToday, isTomorrow, isYesterday, isPast } from 'date-fns';
  import ProForecastSpotForecast from '@/components/ProForecastSpotForecast.vue';
  import ProForecastCredits from '@/components/ProForecastCredits.vue';
  import SegmentedControl from "@/components/controls/SegmentedControl.vue";
  import SegmentedControlButton from "@/components/controls/SegmentedControlButton.vue";
  import ToggleList from "@/components/controls/ToggleList.vue";   
  import ToggleItem from "@/components/controls/ToggleItem.vue";   
  import Popup from '@/components/controls/Popup.vue';
  import ModalProforecastHelp from '@/components/ModalProforecastHelp.vue';  
  import ControlButton from '@/components/controls/ControlButton.vue';
  import Icon from '@/components/icons/Icon.vue';

  const props = defineProps({
    spotForecasts: {
      type: Array,
      required: true
    },
    forecaster: {
      type: String,
      required: true
    },
    forecasterInfo: {
      type: Object,
      required: false
    },
    validDate: {
      type: String,
      required: true
    },
    isSample: {
      type: Boolean,
      default: false
    },
    spotForecastSettings: {
      type: Object,
      default: () => {}
    }
  });

  const proForecastStore = useProForecastStore();
  const getSpotForecastState = (spotId) => {
    return proForecastStore.spotForecastState ? proForecastStore.spotForecastState[`${spotId}`] : null;
  };

  const showPopup = ref(false);
  const showDirectionArrow = ref(proForecastStore.spotForecastSettings.showDirectionArrow)
  const showDirectionValue = ref(proForecastStore.spotForecastSettings.showDirectionValue)
  const directionValueType = ref(proForecastStore.spotForecastSettings.directionValueType)
  const showProForecastHelpModal = ref(false);

  const blockDateHeader = computed(() => {
    if (props.isSample) return "Today's Forecast";

    const date = new Date(props.validDate);

    if (isToday(date)) {
      return "Today's Spot Forecasts";
    } else if (isTomorrow(date)) {
      return "Tomorrow's Spot Forecasts";
    } else if (isYesterday(date)) {
      return "Yesterdays's Spot Forecasts";
    } else if (isPast(date)) {
      return "Previous Spot Forecasts";
    } else {
      return "Upcoming Spot Forecasts";
    }
  });
  
  const handleAccordianToggle = (e) => {
    if (props.isSample) return;
    
    const { collapsed, spotId } = e;

    const spotState = {
      ...proForecastStore.spotForecastState,
      [spotId]: {
        ...proForecastStore.spotForecastState[spotId],
        collapsed: collapsed
      }
    }

    proForecastStore.spotForecastState = spotState;
  }


  const handleDirectionArrowChange = (e) => {
    proForecastStore.spotForecastSettings.showDirectionArrow = showDirectionArrow.value;
  }

  const handleDirectionValueChange = (e) => {
    proForecastStore.spotForecastSettings.showDirectionValue = showDirectionValue.value;
  }

  const handleDirectionValueTypeChange = (e) => {
    proForecastStore.spotForecastSettings.directionValueType = directionValueType.value;
  }

  watchEffect(() => {
    showDirectionArrow.value = proForecastStore.spotForecastSettings.showDirectionArrow
    showDirectionValue.value = proForecastStore.spotForecastSettings.showDirectionValue
    directionValueType.value = proForecastStore.spotForecastSettings.directionValueType  
  })
</script>

<template>
  <section v-if="proForecastStore.spotForecastStateLoading" class="jwx-page__section" data-divider="true">
    <div class="jwx-page__section-header" data-has-actions="true">
      <h2 class="jwx-page__section-title">{{ blockDateHeader }}</h2>
      <p class="jwx-page__section__header-info jwx-font-size-sm jwx-text-color-subtle jwx-font-weight-400 jwx-m-right-base">Wind speed: kts</p>
      <div class="jwx-page__section-header-actions">
        <Popup
          id="spot-forecast-settings-menu"
          icon="more-menu"
          :showPopup="showPopup"
          class="jwx-page__section-header-action"
        >
          <template #button-content>
          <div class="jwx-popup__button">
            <Icon name="more-menu" class="jwx-user-favorite__menu-icon" />
            <span class='sr-only'>Open Spot Forecast Settings Menu</span>
          </div>
          </template>
          <template #menu-content>
            <div class="jwx-popup-menu">            
              <div class="jwx-action-menu">
                <ToggleList>
                  <ToggleItem
                    @change="handleDirectionArrowChange"
                    :value="proForecastStore.spotForecastSettings.showDirectionArrow"
                    iconName="station-marker"
                    id="settings-show-dir-arrow"
                    v-model.number="showDirectionArrow"
                  >
                    Show Direction Arrows
                  </ToggleItem>                   
                  <ToggleItem
                    @change="handleDirectionValueChange"
                    :value="proForecastStore.spotForecastSettings.showDirectionValue"
                    iconName="compass"
                    id="settings-show-dir-value"
                    v-model.number="showDirectionValue"
                  >
                    Show Direction Value
                  </ToggleItem>   
                  <SegmentedControl
                    @click="handleDirectionValueTypeChange"
                    :value="proForecastStore.spotForecastSettings.directionValueType"
                    :disabled="proForecastStore.spotForecastSettings.showDirectionValue === false"
                    v-model="directionValueType"
                    label="Select the time period of the onsite reports to show."
                  >
                    <SegmentedControlButton value="cardinal">Cardinal</SegmentedControlButton>
                    <SegmentedControlButton value="degrees">Degrees</SegmentedControlButton>
                  </SegmentedControl>                
                </ToggleList>
              </div>
            </div>
          </template>
        </Popup>
        <div class="jwx-page__section-header-action">
          <ControlButton
            @click.prevent="showProForecastHelpModal = !showProForecastHelpModal"
            data-surface="1"
            data-shape="circle"
            style="--jwx-button-control-min-width: 1.5rem; --jwx-button-control-icon-max-size: .875rem; --jwx-button-control-text-color: var(--jwx-text-color-subtle)"
          >
            <Icon name="question-mark" class="jwx-button-control__icon" />
          </ControlButton>     
        </div>        
      </div>
      <ProForecastCredits 
        v-if="proForecastStore.showDailyForecast" 
        :forecaster="props.forecaster" 
        :forecasterInfo="props.forecasterInfo"
      />
    </div>
    <ProForecastSpotForecast
      v-for="spotForecast in props.spotForecasts"
      @accordian:toggle="handleAccordianToggle($event)"
      :spotForecast="spotForecast"
      :spotForecastState="getSpotForecastState(spotForecast.spot_id)"
      :spotForecastSettings="props.spotForecastSettings"
      :key="spotForecast.spot_id"
    >
    </ProForecastSpotForecast>
  </section>
  <ModalProforecastHelp 
    v-if="showProForecastHelpModal"
    :show="showProForecastHelpModal"
    @close="showProForecastHelpModal = false"
  />  
</template>